/**
 * Do not call.
 *
 * Numbers here are never dialled by this app, whatever arrives for them. Three ways
 * a number lands on this list: the hook reported `dnc` or `wrong_number`, an FDE
 * added it by hand, or it hit its attempt cap on a channel.
 *
 * Removing an entry only makes the number dialable again — it does not re-queue
 * anything, and the page says so, because "I took them off the list and nothing
 * happened" is otherwise a reasonable thing to conclude.
 *
 * ## Why it is searched and paged
 *
 * Arun, 2026-09-02: *"this will be a big list after a few days. please ensure you eitehr put
 * this in separate tabs and paginate them oterhwise this page will not load well."* This list
 * is the extreme case of that: it only ever grows — nothing takes a number off it but a person
 * — so on a busy account it is the first screen that stops loading.
 *
 * Paging alone would not be enough, and would in fact be worse than the unbounded version:
 * the question this page exists to answer is *"is this number on the list?"*, and forty clicks
 * of Next is not an answer. So the search runs on the server, over the whole table, and the
 * count in the section title is the server's count for whatever filter is in force — never
 * `rows.length`, which is the size of one page and would have read as the size of the list.
 *
 * Tabs, the other half of what he offered, would be wrong here: the four reasons are not four
 * jobs. Somebody checking a number does not know which reason put it there, and splitting the
 * table by reason would make them look four times.
 */

import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DateTime } from "luxon";
import { AppShell } from "@/components/app-shell";
import { AccountSwitcher } from "@/components/account-switcher";
import { PageHeader } from "@/components/ui/page-header";
import { SettingsSection } from "@/components/ui/settings-section";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ActionBarSearch } from "@/components/ui/action-bar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaginationBar } from "@/components/ui/pagination-bar";
import { useParams } from "react-router-dom";
import { errorMessage } from "@/lib/api";
import {
  useAccountConfig,
  useAddSuppression,
  useRemoveSuppression,
  useSuppressions,
} from "@/lib/queries";

const REASON_LABEL: Readonly<Record<string, string>> = {
  dnc: "asked not to be called",
  wrong_number: "wrong number",
  max_attempts: "reached the attempt cap",
  manual: "added by hand",
};

const DEFAULT_LIMIT = 50;

export function SuppressionsPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const { data: config } = useAccountConfig(accountId);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  /*
    No debounce, and no local filtering. The hook keeps the previous page on screen while the
    next one loads (`placeholderData`), so typing does not flash a skeleton, and the search box
    pulses instead — the same contract the callback queue's search uses. Filtering in the
    browser would only ever search the page you can already see, which is the bug this replaced.
  */
  const { data, isPending, isFetching, isError, error, refetch } = useSuppressions(accountId, {
    limit,
    offset: page * limit,
    q: search,
  });
  const add = useAddSuppression(accountId);
  const remove = useRemoveSuppression(accountId);
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    document.title = config?.account.name ? `Do not call · ${config.account.name}` : "Do not call";
  }, [config?.account.name]);

  const rows = data?.suppressions ?? [];
  const total = data?.total ?? 0;

  /*
    Removing the only row on the last page leaves you looking at an empty table with a working
    Previous button, which reads as "the list is gone". Clamping here rather than in the remove
    handler catches every cause — a delete, a narrower search, a smaller page size.
  */
  const maxPage = Math.max(Math.ceil(total / limit) - 1, 0);
  useEffect(() => {
    if (data && page > maxPage) setPage(maxPage);
  }, [data, page, maxPage]);

  /*
    The server searches `phone_number LIKE %digits%` after stripping everything that is not a
    digit — which means a search with no digits in it is not a failed search, it is *no* search,
    and it returns the whole list. Silently. Somebody typing a patient's name would read that
    as "every one of these matches", so the box says what it matched on instead.
  */
  const typed = search.trim();
  const digits = typed.replace(/\D/g, "");
  const searchIgnored = typed !== "" && digits === "";
  const searching = digits !== "";

  /*
    The rows scroll inside their own box, and the box is the same DOM node from one page to the
    next — so without this, Next leaves you forty rows down page two, looking at the middle of a
    list you have not seen the top of. Reset on the page size and the search too: both replace
    the contents wholesale.
  */
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [page, limit, digits]);

  return (
    <AppShell active="suppressions">
      <div className="flex flex-col h-full w-full max-w-full">
        <PageHeader
          breadcrumb={[
            { label: "Accounts", href: "/accounts" },
            { label: config?.account.name ?? "Account" },
          ]}
          title="Do not call"
          description="Numbers this app will never dial for this account, whatever the ingest hook reports for them."
          actionsSlot={<AccountSwitcher />}
        />

        <div className="min-h-0 flex-1 overflow-auto px-6 py-6">
          <div className="max-w-3xl space-y-6">
            <SettingsSection
              title="Add a number"
              description="Country code optional — the account default is applied to a local number."
              footer={
                <Button
                  size="sm"
                  className="min-w-[120px]"
                  disabled={!phone || add.isPending}
                  onClick={() =>
                    add.mutate(
                      { phone_number: phone, reason: "manual", note: note || null },
                      {
                        onSuccess: (r) => {
                          setPhone("");
                          setNote("");
                          /*
                            Back to the first page: the list is newest-first, so that is the one
                            page the number just added is guaranteed to be on. Adding a number
                            and not seeing it appear anywhere is indistinguishable from the add
                            having failed.
                          */
                          setPage(0);
                          toast.success((r as { message?: string }).message ?? "Added.");
                        },
                        onError: (e) => toast.error(errorMessage(e)),
                      },
                    )
                  }
                >
                  <Plus className="size-3.5" />
                  Add
                </Button>
              }
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Phone number</Label>
                  <Input
                    id="phone"
                    value={phone}
                    placeholder={config?.account.default_country_code ?? "+91"}
                    className="font-mono"
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="note">Note (optional)</Label>
                  <Input
                    id="note"
                    value={note}
                    placeholder="Why, for whoever finds this later"
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
              </div>
            </SettingsSection>

            {/*
              The description this file's own header promised and never rendered:
              "Removing an entry only makes the number dialable again — it does not
              re-queue anything, and the page says so, because 'I took them off the list
              and nothing happened' is otherwise a reasonable thing to conclude."
            */}
            <SettingsSection
              /*
                The server's count for the filter in force, never `rows.length` — which is the
                size of one page and read, wrongly and confidently, as the size of the list.
                Under a search the word changes with it: "Matching" is what makes the number
                honest, because 3 is the truth about the search and a lie about the account.
              */
              title={
                searching
                  ? `Matching numbers (${total.toLocaleString()})`
                  : `Suppressed numbers (${total.toLocaleString()})`
              }
              description="Removing one only makes the number dialable again. It does not re-queue the callbacks that were skipped while it was on the list — those stay closed until the tenant's agent reports the outcome again."
            >
              {/*
                Search over the whole table, not the page. This is the control that answers the
                only urgent question here — "will this app ring this number?" — so it sits above
                the list rather than in a header band the section has no slot for.
              */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <ActionBarSearch
                  value={search}
                  onValueChange={(v) => {
                    setSearch(v);
                    setPage(0);
                  }}
                  onClear={() => {
                    setSearch("");
                    setPage(0);
                  }}
                  placeholder="Any part of a number"
                  aria-label="Search suppressed numbers"
                  pending={isFetching}
                  className="w-64"
                />
                {searchIgnored ? (
                  <p className="text-warning text-xs">
                    Numbers only — there is nothing to match on in that, so the whole list is
                    shown.
                  </p>
                ) : searching ? (
                  <p className="text-muted-foreground text-xs">
                    Matching <span className="font-mono">{digits}</span> anywhere in the number,
                    across every page.
                  </p>
                ) : null}
              </div>

              {isError ? (
                <ErrorState
                  title="Couldn't load the list"
                  description={errorMessage(error)}
                  onRetry={() => refetch()}
                />
              ) : isPending ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : rows.length === 0 ? (
                /*
                  Two different facts, and conflating them is the one mistake this page cannot
                  make: an empty account means nothing is suppressed, an empty *search* means
                  this app will ring that number. Say the second one out loud.
                */
                searching ? (
                  <EmptyState
                    size="compact"
                    title="No suppressed number contains those digits"
                    description="Nothing on this account's do-not-call list matches. If that was the number you were checking, this app will dial it when a callback arrives for it."
                  />
                ) : (
                  <EmptyState
                    size="compact"
                    title="Nothing suppressed"
                    description="Numbers appear here when the hook reports do-not-call or a wrong number, when an FDE adds one, or when a number reaches its attempt cap on a channel."
                  />
                )
              ) : (
                /*
                  The list scrolls inside its own frame rather than lengthening the page. Fifty
                  rows is about 2,800px, which put the pager — and the count, and the page-size
                  control — three screens below the fold: everything added here to make a long
                  list usable was itself only reachable by scrolling the long list. Bounded, the
                  controls stay on screen and the page behind barely moves.
                */
                <div className="rounded-lg border border-border">
                  <div
                    ref={listRef}
                    className="max-h-[32rem] divide-y divide-border overflow-y-auto"
                  >
                    {rows.map((s) => (
                      <div key={s.phone_number} className="flex items-center gap-4 px-4 py-2.5">
                        <span className="w-40 shrink-0 font-mono text-sm">{s.phone_display}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm">{REASON_LABEL[s.reason] ?? s.reason}</p>
                          {s.note && (
                            <p className="truncate text-xs text-muted-foreground">{s.note}</p>
                          )}
                        </div>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {DateTime.fromISO(s.created_at)
                            .setZone(config?.account.timezone ?? "utc")
                            .toFormat("d LLL yyyy")}
                        </span>
                        {/*
                          Taking a number off this list is the one action on this page that
                          can hurt somebody: for a `dnc` row it means this app may ring a
                          person who explicitly asked it not to. So it is red, it asks first,
                          and the question names the number and why it was suppressed.

                          `remove.variables === s.phone_number` rather than `remove.isPending`:
                          the mutation is shared by every row, so a bare isPending greys out
                          all forty trash buttons while one of them works, which reads as the
                          page having broken rather than one row being busy.
                        */}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              aria-label={`Allow calls to ${s.phone_number} again`}
                              disabled={remove.variables === s.phone_number && remove.isPending}
                            >
                              {remove.variables === s.phone_number && remove.isPending ? (
                                <Loader2 className="size-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="size-3.5" />
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Allow calls to {s.phone_display} again?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {s.reason === "dnc"
                                  ? "This number is on the list because the person asked not to be called. Taking it off means this app may dial them again — do it only if you know that request was recorded in error."
                                  : s.reason === "wrong_number"
                                    ? "This number was recorded as belonging to the wrong person. Taking it off means this app may dial whoever actually answers it."
                                    : s.reason === "max_attempts"
                                      ? "This number reached its attempt cap. Taking it off lets the app start dialling it again the next time an outcome arrives for it."
                                      : "This number was added by hand. Taking it off makes it dialable again."}
                                {s.note ? ` The note on it reads: “${s.note}”.` : ""}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Leave it suppressed</AlertDialogCancel>
                              <AlertDialogAction
                                variant="destructive"
                                onClick={() =>
                                  remove.mutate(s.phone_number, {
                                    onSuccess: (r) =>
                                      toast.success(
                                        (r as { message?: string }).message ?? "Removed.",
                                      ),
                                    onError: (e) => toast.error(errorMessage(e)),
                                  })
                                }
                              >
                                Allow calls again
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ))}
                  </div>
                  {/*
                    Outside the scroll box, inside the border: the pager belongs to this list, so
                    it sits in its frame — and staying out of the scrolling part is what keeps it
                    on screen while the rows move under it. Its own `border-t` closes the box.
                  */}
                  <PaginationBar
                    page={page}
                    limit={limit}
                    total={total}
                    onPageChange={setPage}
                    onLimitChange={(n) => {
                      setLimit(n);
                      setPage(0);
                    }}
                    limitOptions={[25, 50, 100, 200]}
                  />
                </div>
              )}
            </SettingsSection>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
